"use client";

import { TypeImage, TypeImageCollection } from "@contentful/types";
import { Container, ContainerProps } from "@yardzen/ui/components/container";
import { H1 } from "@yardzen/ui/components/h1";
import Image from "next/image";
import { useState } from "react";
import { SelectorPills } from "./SelectorPills";

/** Contentful asset URLs come back protocol-relative. */
function withProtocol(url: string | undefined) {
  if (!url) return undefined;
  return url.startsWith("//") ? `https:${url}` : url;
}

/** Widths the Container offers, mapped to the `sizes` hint each one implies. */
const SIZE_HINTS: Record<string, string> = {
  sm: "(max-width: 640px) 100vw, 640px",
  md: "(max-width: 980px) 100vw, 980px",
  lg: "(max-width: 1200px) 100vw, 1200px",
  xl: "(max-width: 1600px) 100vw, 1600px",
  full: "100vw",
};

/**
 * The `tabs` variant of ImageCollection: a heading, a row of pills, and one
 * image at a time. Same shape as Before & After, and it reuses that section's
 * `SelectorPills`. Tab text comes from each Image entry's `label`, alt from
 * its `description`.
 *
 * `heading` renders as the section title here. The carousel variant renders
 * the same field small, beside an avatar, as a designer's name — worth knowing
 * before you widen either one.
 *
 * @param imageCollection - the `imageCollection` entry
 * @param images - the collection's resolved Image entries
 * @param size - Container width
 */
export function ImageCollectionTabs({
  imageCollection,
  images,
  size = "lg",
}: {
  imageCollection: TypeImageCollection<"WITHOUT_UNRESOLVABLE_LINKS">;
  images: TypeImage<"WITHOUT_UNRESOLVABLE_LINKS">[];
  size?: ContainerProps["size"];
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // An unpublished asset leaves `image` unresolved, which would render an
  // empty tab rather than simply not offering it.
  const slides = images
    .map((entry, index) => {
      const url = withProtocol(entry.fields.image?.fields.file?.url);
      if (!url) return null;
      const details = entry.fields.image?.fields.file?.details.image;
      return {
        id: entry.sys.id,
        label: entry.fields.label || `Image ${index + 1}`,
        alt: entry.fields.description || "",
        url,
        width: details?.width,
        height: details?.height,
      };
    })
    .filter((slide): slide is NonNullable<typeof slide> => slide !== null);

  if (!slides.length) {
    return null;
  }

  const activeIndex = Math.min(currentIndex, slides.length - 1);
  const slide = slides[activeIndex];

  const { heading, subheading } = imageCollection.fields;

  return (
    <Container size={size} className="mt-12 flex flex-col items-center gap-6">
      {/* The heading belongs to the tabs under it, so the two sit in their own
          group with a tighter gap than the section's. */}
      {(heading || slides.length > 1) && (
        <div className="flex w-full flex-col items-center gap-3">
          {heading && <H1 className="pb-0 text-center">{heading}</H1>}
          {subheading && (
            <p className="text-brand-secondary-dark font-body max-w-2xl text-center">
              {subheading}
            </p>
          )}
          {slides.length > 1 && (
            <SelectorPills
              label={heading || "Images"}
              selectedIndex={activeIndex}
              onSelect={setCurrentIndex}
              options={slides.map(({ id, label }) => ({ id, label }))}
            />
          )}
        </div>
      )}

      <Image
        key={slide.id}
        src={slide.url}
        alt={slide.alt}
        width={slide.width || 1920}
        height={slide.height || 1080}
        className="animate-fadeIn h-auto w-full"
        priority={false}
        quality={90}
        sizes={SIZE_HINTS[size ?? "lg"]}
      />
    </Container>
  );
}
